import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { FILTER_TEMPLATES, FilterBarService } from '@modules/filters/filter-bar.service';
import { FilterBarComponent } from '@modules/filters/filter-bar/filter-bar.component';
import { UserSequenceGalleryService } from '@modules/gallery/services/dialogs/sliders/sequences/user-sequence-gallery.service';
import { SequenceListComponent } from '@modules/sequence/sequence-list/sequence-list.component';
import { SequencePreviewModeSegmentedComponent } from '@modules/sequence/sequence-preview-mode-segmented/sequence-preview-mode-segmented.component';
import { SequenceActionsService } from '@modules/sequence/services/sequence-actions.service';
import { SequencePreviewModeService } from '@modules/sequence/services/sequence-preview-mode.service';
import { LoaderComponent } from '@modules/shared/loader/loader.component';
import { PAGINATED_LIST_SOURCE } from '@modules/shared/paginated-list/paginated-list.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Patient } from '@pixacare/domain';
import { FlattenedSequence } from '@pixacare/dtos';
import { FilterOperator } from '@pixacare/shared';
import { SequenceStorePaginationService } from '@services/sequence-store-pagination.service';
import { SequencePreviewMode } from '@shared/models/enums/sequence-preview-mode.enum';
import { sequenceFilterTemplates } from '@shared/models/filters/filter-template.config';
import { MediaClickedOutput } from '@shared/models/media-clicked-output';
import { MenuAction } from '@shared/models/menu-actions/menu-action';
import { TuiButton } from '@taiga-ui/core';
import { distinctUntilChanged, first, map, switchMap } from 'rxjs';
import { PatientService } from '../patient/patient.service';
import { TuiAnimated } from '@taiga-ui/cdk';

@UntilDestroy()
@Component({
  hostDirectives: [TuiAnimated],
  templateUrl: './patient-sequences.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    FilterBarService,
    {
      provide: FILTER_TEMPLATES,
      useValue: sequenceFilterTemplates,
    },
    {
      provide: PAGINATED_LIST_SOURCE,
      useExisting: SequenceStorePaginationService,
    },
    UserSequenceGalleryService,
  ],
  host: {
    class: 'flex flex-col',
  },
  imports: [
    CommonModule,
    FilterBarComponent,
    SequenceListComponent,
    TuiButton,
    LoaderComponent,
    TranslocoDirective,
    SequencePreviewModeSegmentedComponent,
  ],
})
export class PatientSequencesComponent implements OnInit, OnDestroy {
  private readonly patientPageService = inject(PatientService);
  private readonly filterBarService = inject(FilterBarService);
  private readonly userSequenceGalleryService = inject(UserSequenceGalleryService);
  private readonly sequenceActionsService = inject(SequenceActionsService);
  private readonly sequencePreviewModeService = inject(SequencePreviewModeService);

  patient$ = this.patientPageService.getPatient();
  hasLicenseWriteAccess$ = this.patientPageService.getHasLicenseWriteAccess();
  isSequenceStateLoaded$ = this.patientPageService.getIsSequenceStateLoaded();
  clientCode$ = this.patientPageService.getParams().pipe(map(({ clientCode }) => clientCode));

  sequenceActions: MenuAction<FlattenedSequence>[] = this.sequenceActionsService.getActions();
  sequencePreviewMode: SequencePreviewMode = this.sequencePreviewModeService.getPreviewMode();

  SequencePreviewMode = SequencePreviewMode;

  ngOnInit(): void {
    this.filterBarService.init();
    this.loadSequences();
  }

  ngOnDestroy(): void {
    this.patientPageService.cleanupSequenceSubscription();
  }

  addSequence(patient: Patient): void {
    this.patientPageService
      .getParams()
      .pipe(
        first(),
        switchMap(({ clientCode, departmentId }) => {
          const departmentIds = departmentId ? [departmentId] : [];
          return this.sequenceActionsService.addSequence({ clientCode, patient, departmentIds });
        }),
      )
      .subscribe((created) => {
        if (created) {
          this.filterBarService.clear();
        }
      });
  }

  openSequenceMediaGallery({ clientCode, sequenceId, mediaIdx }: MediaClickedOutput): void {
    this.userSequenceGalleryService.openGallery(clientCode, sequenceId, mediaIdx);
  }

  setPreviewMode(mode: SequencePreviewMode): void {
    this.sequencePreviewMode = mode;
    this.loadSequences();
  }

  loadSequences(): void {
    this.patientPageService
      .getParams()
      .pipe(
        untilDestroyed(this),
        map(({ patientId }) => patientId),
        distinctUntilChanged(),
      )
      .subscribe((patientId) => {
        this.filterBarService.setPersistentFilters([
          {
            prop: 'patient_id',
            op: FilterOperator.EQUAL,
            val: patientId.toString(),
          },
        ]);
        this.patientPageService.getSequences(this.filterBarService.filteredSearch$);
      });
  }
}
